import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, retry, switchMap, throwError, timer } from 'rxjs';

import { GbeTokensService } from '@gfe/core/services/gbe-tokens/gbe-tokens.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';

import {
  POST_LOGIN_REDIRECT_KEY,
  SESSION_EXPIRED_KEY
} from '@core/constants/auth.constants';
import { occurrenceText } from '@core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@core/services/gbe-error-parser/gbe-error-parser.service';
import { GFE_LOG_REQUEST } from '@core/services/gfe-log/gfe-log.service';
import * as AuthActions from '@core/store/auth/auth.actions';

import { GbeErrorCodes, GbeErrorCodesUtils } from '../models/gbe/gbe-error-codes.enum';
import { GBE_BASE_URL } from '../tokens/gbe-url.token';
import { parseGbeError } from '../utils/parse-gbe-error';

export const GBE_ERROR_MAX_RETRIES = 5;
export const GBE_ERROR_RETRY_DELAY_MS = 100;

// Marks a request that has already been retried after a silent token refresh,
// so a second 401 on the retried request falls through to the full Keycloak
// redirect instead of looping.
const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

// Opt-out for callers that handle GBE errors locally and don't want the
// generic notification to fire. Auth/network handling still runs as usual —
// only the fallthrough notification is skipped.
export const SKIP_GBE_ERROR_NOTIFICATION = new HttpContextToken<boolean>(() => false);

// Opt-out for callers that render a validation failure's messages inline, so the
// validation toast would only repeat them. Other error toasts are unaffected.
export const SKIP_GBE_VALIDATION_NOTIFICATION = new HttpContextToken<boolean>(() => false);

// Opt-out for calls that must never be re-sent: a retry would repeat work the
// first attempt may already have committed. The /api-down navigation still
// happens, so an actually unreachable API is reported as usual.
export const SKIP_GBE_ERROR_RETRY = new HttpContextToken<boolean>(() => false);

// 403s that report a state rather than a missing permission, and so carry a message
// worth more than the generic one. Every other coded 403 keeps the generic message
// deliberately — `forbidden_operation` says nothing a user can act on.
const FORBIDDEN_CODES_WITH_OWN_MESSAGE: readonly GbeErrorCodes[] = [
  GbeErrorCodes.DEPLOY_CHANNEL_DISABLED
];

function triggerFullReauth(
  router: Router,
  tokensService: { clear(): void },
  oidcSecurityService: OidcSecurityService
): void {
  tokensService.clear();
  // Persist the current URL so it can be restored after the Keycloak
  // round-trip; otherwise the user lands on the default dataspace.
  if (router.url && router.url !== '/') {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, router.url);
  }
  // Flag the expired session so the user can be informed (and warned that
  // unsaved changes may have been lost) once they are signed back in. A toast
  // shown here would be wiped by the imminent full-page redirect, so it must be
  // surfaced after re-authentication instead.
  sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true');
  oidcSecurityService.authorize();
}

function isNetworkError(error: HttpErrorResponse): boolean {
  return (
    error.status === 0 ||
    error.status === HttpStatusCode.BadGateway ||
    error.status === HttpStatusCode.ServiceUnavailable ||
    error.status === HttpStatusCode.GatewayTimeout
  );
}

export const gbeErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const tokensService = inject(GbeTokensService);
  const oidcSecurityService = inject(OidcSecurityService);
  const translate = inject(TranslateService);
  const router = inject(Router);
  const store = inject(Store);
  const gbeBaseUrl = inject(GBE_BASE_URL);
  const gbeErrorParser = inject(GbeErrorParserService);

  const isTargeted = req.url.startsWith(gbeBaseUrl);
  if (!isTargeted) return next(req);

  // GFE log requests must bypass error handling entirely: a failed log POST
  // must never trigger notifications, retries, or auth redirects, otherwise
  // we'd risk feeding errors back into the global ErrorHandler.
  if (req.context.get(GFE_LOG_REQUEST)) return next(req);

  return next(req).pipe(
    retry({
      count: GBE_ERROR_MAX_RETRIES,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        if (isNetworkError(error) && !req.context.get(SKIP_GBE_ERROR_RETRY)) {
          return timer(retryCount * GBE_ERROR_RETRY_DELAY_MS);
        }
        throw error;
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (isNetworkError(error)) {
        router.navigate(['/api-down']);
      }

      const parsed = { ...parseGbeError(error), status: error.status };

      // Handle Unknown Error Code
      if (parsed.errorCode === GbeErrorCodes.UNKNOWN_ERROR_CODE) {
        console.error('An unknown error was returned by the API:', error);
      }

      // Recoverable: an expired access token can be refreshed silently via
      // the OIDC refresh token, so the user stays on the page with their
      // in-flight UI state intact.
      const isExpired = parsed.errorCode === GbeErrorCodes.API_TOKEN_EXPIRED_CODE;
      const alreadyRetried = req.context.get(AUTH_RETRIED);

      if (isExpired && !alreadyRetried) {
        return tokensService.refreshAccessToken().pipe(
          switchMap((newToken) => {
            if (!newToken) throw new Error('Silent token refresh returned no token');
            const retried = req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
              context: req.context.set(AUTH_RETRIED, true)
            });
            return next(retried);
          }),
          catchError(() => {
            triggerFullReauth(router, tokensService, oidcSecurityService);
            return throwError(() => parsed);
          })
        );
      }

      // Non-recoverable auth errors (malformed / invalid signature / missing
      // token / etc.) or a retried request that still returned expired: full
      // Keycloak redirect.
      const fatalAuthErrorCodes = [
        GbeErrorCodes.TOKEN_NOT_PROVIDED_CODE,
        GbeErrorCodes.API_TOKEN_EXPIRED_CODE,
        GbeErrorCodes.API_KEY_EXPIRED_CODE,
        GbeErrorCodes.API_TOKEN_MALFORMED_CODE,
        GbeErrorCodes.API_TOKEN_INVALID_SIGNATURE_CODE,
        GbeErrorCodes.API_INVALID_TOKEN
      ];

      if (fatalAuthErrorCodes.some((code) => code === parsed.errorCode)) {
        triggerFullReauth(router, tokensService, oidcSecurityService);
        return throwError(() => parsed);
      }

      // Handle Keycloak user not linked to local account.
      // Must run before the generic 401 handler so we redirect to /login
      // (instead of dispatching a logout that would land on a blank shell).
      // No notification: /login lives outside the app shell and has no
      // notification container, so any notification would be invisible.
      if (parsed.errorCode === GbeErrorCodes.AUTH_USER_NOT_LINKED) {
        tokensService.clear();
        router.navigate(['/login']);
        return throwError(() => parsed);
      }

      // Handle any other 401 (e.g. user disabled/deleted) — full logout
      if (error.status === HttpStatusCode.Unauthorized) {
        tokensService.clear();
        store.dispatch(AuthActions.logout());
        return throwError(() => parsed);
      }

      // Handle Invalid Credentials, should not use notifyService.
      if (parsed.errorCode === GbeErrorCodes.INVALID_CREDENTIALS_CODE) {
        return throwError(() => parsed);
      }

      if (GbeErrorCodesUtils.shouldNotIntercept(parsed.errorCode as GbeErrorCodes)) {
        return throwError(() => parsed);
      }

      // Almost every 403 reduces to the same actionable fact, so this branches on the status
      // rather than the error code: the API returns two different bodies for it, and Nest's
      // default one (`{statusCode, message, error}`) carries no code at all, so it would
      // otherwise reach the fallthrough as "Unknown error". The listed codes are the
      // exception — they describe a state, not a permission, and fall through to their own
      // translated message below.
      if (
        error.status === HttpStatusCode.Forbidden &&
        !FORBIDDEN_CODES_WITH_OWN_MESSAGE.includes(parsed.errorCode as GbeErrorCodes)
      ) {
        if (!req.context.get(SKIP_GBE_ERROR_NOTIFICATION)) {
          notificationService.error(
            translate.instant('gbe_errors.forbidden.title'),
            translate.instant('gbe_errors.forbidden.message')
          );
        }
        return throwError(() => parsed);
      }

      // Handle Validation Failed
      if (parsed.errorCode === GbeErrorCodes.VALIDATION_FAILED) {
        const details = parsed.details as Record<string, unknown> | undefined;
        // `details` reaches the client verbatim in the backend's snake_case, so the
        // per-field messages live under the wire key, not a camel-cased alias.
        const validationErrors = details?.['validation_errors'] as string[] | undefined;
        const message = validationErrors?.length
          ? validationErrors.join(', ')
          : parsed.errorMessage;
        if (!req.context.get(SKIP_GBE_VALIDATION_NOTIFICATION)) {
          notificationService.error(
            translate.instant('gbe_errors.validation_failed.title'),
            message
          );
        }
        return throwError(() => parsed);
      }

      return throwError(() => {
        if (!req.context.get(SKIP_GBE_ERROR_NOTIFICATION)) {
          const views = gbeErrorParser.resolveError(parsed);
          notificationService.error(
            views[0]?.title ?? parsed.errorCode ?? '',
            views
              .flatMap((view) => view.occurrences)
              .map((occurrence) => occurrenceText(occurrence))
              .join(', ')
          );
        }
        return parsed;
      });
    })
  );
};
